import type { PreviewSupplierSource } from '@travel-planner/shared/schemas';
import { trpc } from '@/shared/api/trpc';
import { toastStore } from '@/shared/ui';
import { SupplierDocumentsPanel } from './supplier-documents-panel';
import { SupplierNotesPanel } from './supplier-notes-panel';

// Melhoria 7 §4: the destination is built from the immutable CRM id already on
// the wire — never by matching supplier name, email or website.
export const crmRecordHref = (companyId: string) =>
  `/crm/accounts/${companyId}?pane=info`;

type SupplierNotesSectionProps = {
  // The product or itinerary item whose supplier is shown. The server resolves
  // the supplier from that record — the caller never names it.
  source: PreviewSupplierSource;
  canOpenCrm: boolean;
};

/**
 * The supplier's Contract Year + CRM Notes (T-196), read live on every mount so
 * a CRM edit shows on the next open. Mount it only while its tab is shown — the
 * query runs on mount, so the data is fetched when the tab is first selected.
 * Shared by the product preview and the planner's item preview (T-287).
 */
export const SupplierNotesSection = ({
  source,
  canOpenCrm,
}: SupplierNotesSectionProps) => {
  const { data, isLoading, isError } =
    trpc.products.previewSupplierNotes.useQuery(source, { staleTime: 0 });

  // Built from the supplier id the SERVER resolved for these notes, so the
  // record the link opens is the record the notes came from (Melhoria 7 §9).
  // `null` when there is nothing safe to open or the caller may not read CRM —
  // presentation only, the CRM route runs its own server-side check.
  const crmHref =
    canOpenCrm && data?.linked && !data.unavailable && data.supplierId
      ? crmRecordHref(data.supplierId)
      : null;

  return (
    <SupplierNotesPanel
      crmHref={crmHref}
      data={data}
      isError={isError}
      isLoading={isLoading}
    />
  );
};

type SupplierDocumentsSectionProps = {
  source: PreviewSupplierSource;
};

/**
 * The supplier's product-available CRM documents (Melhoria 6), read live on
 * every mount. Opening one asks the server for a short-lived URL naming only
 * the source record and the document. Mount it only while its tab is shown.
 */
export const SupplierDocumentsSection = ({
  source,
}: SupplierDocumentsSectionProps) => {
  const { data, isLoading, isError } =
    trpc.products.previewSupplierDocuments.useQuery(source, { staleTime: 0 });

  const openDocument = trpc.products.previewSupplierDocumentUrl.useMutation();

  // The window is opened synchronously on the click — deferring it into the
  // promise callback would make it a popup the browser blocks.
  const onOpen = (documentId: string) => {
    const newTab = window.open('', '_blank', 'noopener,noreferrer');
    openDocument
      .mutateAsync({ ...source, documentId })
      .then(({ url }) => {
        if (newTab) {
          newTab.location.href = url;
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      })
      .catch(() => {
        newTab?.close();
        toastStore.getState().show({
          message: 'This document could not be opened. Please try again.',
        });
      });
  };

  return (
    <SupplierDocumentsPanel
      data={data}
      isError={isError}
      isLoading={isLoading}
      isOpening={openDocument.isPending}
      onOpen={onOpen}
    />
  );
};
